import React, { useState } from 'react'
import { api, setAuthToken, setActiveTenant } from '../api'
import { CurrentUserProfile } from '../types'

interface Props {
  onLoginSuccess: (profile: CurrentUserProfile) => void
}

export function LoginView({ onLoginSuccess }: Props) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [tenantSlug, setTenantSlug] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showSlugField, setShowSlugField] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !password) {
      setError('Lütfen e-posta ve şifrenizi girin.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const res = await api.login({
        email: email.trim(),
        password,
        tenant_slug: tenantSlug.trim() || undefined,
      })

      setAuthToken(res.access_token)
      setActiveTenant(res.tenant.id)

      const profile = await api.getMe()
      onLoginSuccess(profile)
    } catch (err: any) {
      setError(err.message || 'Giriş yapılamadı. Lütfen bilgilerinizi kontrol edin.')
    } finally {
      setLoading(false)
    }
  }

  const handleQuickFill = (quickEmail: string, quickPass: string, quickSlug: string) => {
    setEmail(quickEmail)
    setPassword(quickPass)
    setTenantSlug(quickSlug)
    setShowSlugField(true)
    setError(null)
  }

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Brand Card Header */}
        <div className="login-header">
          <div className="login-logo" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <h1 className="login-title">Parel Cost ERP</h1>
          <p className="login-subtitle">Maliyet Analizi ve Fiyatlandırma Yönetim Sistemi</p>
        </div>

        {/* Login Card */}
        <div className="login-card">
          <h2 className="login-card-title">Giriş Yap</h2>
          <p className="login-card-desc">Tenant kullanıcı hesabınızla oturum açın</p>

          {error && (
            <div className="alert alert-danger" role="alert">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="login-email">E-posta Adresi</label>
              <input
                id="login-email"
                name="email"
                type="email"
                className="form-control"
                placeholder="ornek@sirket.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                spellCheck={false}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <div className="label-with-action">
                <label htmlFor="login-password">Şifre</label>
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                >
                  {showPassword ? 'Gizle' : 'Göster'}
                </button>
              </div>
              <div className="password-input-wrapper">
                <input
                  id="login-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  className="form-control"
                  placeholder="••••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {showSlugField ? (
              <div className="form-group">
                <div className="label-with-action">
                  <label htmlFor="login-slug">Organizasyon / Tenant Kodu (Opsiyonel)</label>
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => setShowSlugField(false)}
                    tabIndex={-1}
                  >
                    Gizle
                  </button>
                </div>
                <input
                  id="login-slug"
                  name="tenant_slug"
                  type="text"
                  className="form-control"
                  placeholder="firma-kodu"
                  value={tenantSlug}
                  onChange={(e) => setTenantSlug(e.target.value)}
                  autoComplete="organization"
                  spellCheck={false}
                  disabled={loading}
                />
              </div>
            ) : (
              <div className="slug-toggle-row">
                <button
                  type="button"
                  className="btn-link slug-toggle-btn"
                  onClick={() => setShowSlugField(true)}
                >
                  + Organizasyon kodu ile giriş yap (opsiyonel)
                </button>
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading}
            >
              {loading ? 'Giriş yapılıyor…' : 'Oturum Aç'}
            </button>
          </form>

          {/* Quick Demo Credentials for Fast Evaluation (Dev only) */}
          {import.meta.env.DEV && (
            <div className="quick-fill-section">
              <div className="divider-label">
                <span>Hızlı Test / Demo Hesapları</span>
              </div>
              <div className="quick-fill-buttons">
                <button
                  type="button"
                  className="btn-quick"
                  onClick={() => handleQuickFill('admin@pool.demo', 'admin123', 'standart-pool')}
                  disabled={loading}
                >
                  <span className="badge badge-info">Demo 1</span>
                  <span className="quick-title">admin@pool.demo</span>
                </button>
                <button
                  type="button"
                  className="btn-quick"
                  onClick={() => handleQuickFill('admin@enterprise.silo', 'silo123', 'enterprise-silo')}
                  disabled={loading}
                >
                  <span className="badge badge-info">Demo 2</span>
                  <span className="quick-title">admin@enterprise.silo</span>
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  )
}
